import { resolveTimeZone } from '@scrambl/shared'

// Intl constructors are expensive in repeated date/message cells. Keep bounded
// caches of immutable timezone rules and formatters, never formatted dates.
const resolvedZones = new Map<string, string | null>()
const abbreviations = new Map<string, Intl.DateTimeFormat>()
const MAX_ZONES = 32

function cache<T>(values: Map<string, T>, key: string, value: T) {
  if (values.size >= MAX_ZONES) values.delete(values.keys().next().value!)
  values.set(key, value)
}

export function resolveUserTimezone(preferred?: string | null): string {
  if (preferred && !/^[+-]/.test(preferred)) {
    if (!resolvedZones.has(preferred)) {
      try {
        cache(
          resolvedZones,
          preferred,
          new Intl.DateTimeFormat('en-US', {
            timeZone: preferred,
          }).resolvedOptions().timeZone,
        )
      } catch {
        cache(resolvedZones, preferred, null)
      }
    }
    const resolved = resolvedZones.get(preferred)
    if (resolved) return resolved
  }
  // Re-read the device zone when no valid profile preference is available so
  // changing the OS timezone still takes effect on the next render.
  return resolveTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone)
}

export function getTimezoneAbbreviation(
  timezone: string,
  date = new Date(),
): string {
  try {
    let formatter = abbreviations.get(timezone)
    if (!formatter) {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short',
      })
      cache(abbreviations, timezone, formatter)
    }
    return (
      formatter.formatToParts(date).find(part => part.type === 'timeZoneName')
        ?.value || timezone
    )
  } catch {
    return timezone
  }
}
